// Generic editor for any AnyEvent. Adapts the field set to the type and
// PATCHes the appropriate /api endpoint on save.

import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import type { AnyEvent } from '@shared/types.ts';
import type {
  BottleContent,
  DiaperColor,
  DiaperConsistency,
  DiaperKind,
  PumpSide,
  SleepLocation,
} from '@shared/constants.ts';
import {
  BOTTLE_CONTENTS,
  DIAPER_COLORS,
  DIAPER_CONSISTENCIES,
  DIAPER_KINDS,
  PUMP_SIDES,
  SLEEP_LOCATIONS,
} from '@shared/constants.ts';
import { Icon } from '../Icon.tsx';
import { SheetShell, FieldRow, SegRow, ToggleSwitch } from './SheetShell.tsx';
import { TimePicker } from './TimePicker.tsx';
import { api } from '../../api/client.ts';
import { useInvalidateEvents } from '../../api/events.ts';
import { parseSegments } from '../../lib/breast.ts';
import type { BreastSegment } from '@shared/types.ts';

type Props = {
  open: boolean;
  onClose: () => void;
  event: AnyEvent | null;
};

const cap = (s: string) => s[0]?.toUpperCase() + s.slice(1);

export function EventEditSheet({ open, onClose, event }: Props) {
  const inv = useInvalidateEvents();
  // Type-erased state buckets. We populate the ones relevant to the event.
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [endedAt, setEndedAt] = useState<number | null>(null);
  const [occurredAt, setOccurredAt] = useState<number | null>(null);
  const [notes, setNotes] = useState<string>('');
  const [location, setLocation] = useState<SleepLocation>('cot');
  const [amount, setAmount] = useState<number>(0);
  const [content, setContent] = useState<BottleContent>('expressed');
  const [pumpSide, setPumpSide] = useState<PumpSide>('both');
  const [yieldLeft, setYieldLeft] = useState<number>(0);
  const [yieldRight, setYieldRight] = useState<number>(0);
  const [diaperKind, setDiaperKind] = useState<DiaperKind>('pee');
  const [consistency, setConsistency] = useState<DiaperConsistency | null>(null);
  const [color, setColor] = useState<DiaperColor | null>(null);
  const [leak, setLeak] = useState<boolean>(false);
  // Breast per-side editor state. Minutes (rounded display); we rebuild
  // segments from these on save.
  const [leftMin, setLeftMin] = useState<number>(0);
  const [rightMin, setRightMin] = useState<number>(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !event) return;
    setNotes((event as { notes?: string | null }).notes ?? '');
    if (event._t === 'sleep') {
      setStartedAt(event.started_at);
      setEndedAt(event.ended_at);
      setLocation((event.location as SleepLocation) ?? 'cot');
    } else if (event._t === 'feed') {
      setStartedAt(event.started_at);
      setEndedAt(event.ended_at);
      setAmount(event.amount_ml ?? 0);
      setContent((event.content as BottleContent) ?? 'expressed');
      const segs = parseSegments(event.segments);
      let lSec = 0;
      let rSec = 0;
      for (const s of segs) {
        const d = Math.max(0, s.to - s.from);
        if (s.side === 'left') lSec += d;
        else rSec += d;
      }
      setLeftMin(Math.round(lSec / 60));
      setRightMin(Math.round(rSec / 60));
    } else if (event._t === 'pump') {
      setStartedAt(event.started_at);
      setEndedAt(event.ended_at);
      setPumpSide((event.side as PumpSide) ?? 'both');
      setYieldLeft(event.yield_left_ml ?? 0);
      setYieldRight(event.yield_right_ml ?? 0);
    } else if (event._t === 'diaper') {
      setOccurredAt(event.occurred_at);
      setDiaperKind(event.kind);
      setConsistency(event.consistency ?? null);
      setColor(event.color ?? null);
      setLeak(!!event.leak);
    }
  }, [open, event?.id]);

  if (!event) return null;

  const isBreast = event._t === 'feed' && (event.type === 'breast_left' || event.type === 'breast_right');
  const isBottle = event._t === 'feed' && event.type === 'bottle';
  const isSolid = event._t === 'feed' && event.type === 'solid';
  const isPumpLeft = pumpSide === 'left';
  const isPumpRight = pumpSide === 'right';

  async function save() {
    if (!event) return;
    setSaving(true);
    try {
      const patch: Record<string, unknown> = {};
      const trimmedNotes = notes.trim();

      if (event._t === 'sleep') {
        if (startedAt !== event.started_at && startedAt != null) patch.started_at = startedAt;
        if ((endedAt ?? null) !== event.ended_at) patch.ended_at = endedAt;
        if (location !== event.location) patch.location = location;
        if ((event.notes ?? '') !== trimmedNotes) patch.notes = trimmedNotes || null;
        if (Object.keys(patch).length) await api(`/api/sleep/${event.id}`, { method: 'PATCH', json: patch });
      } else if (event._t === 'feed') {
        if (startedAt !== event.started_at && startedAt != null) patch.started_at = startedAt;
        if ((endedAt ?? null) !== event.ended_at) patch.ended_at = endedAt;
        if (isBottle) {
          if ((event.amount_ml ?? 0) !== amount) patch.amount_ml = amount;
          if ((event.content ?? null) !== content) patch.content = content;
        }
        if (isBreast) {
          const effStart = startedAt ?? event.started_at;
          const effEnd = endedAt ?? event.ended_at;
          const prev = parseSegments(event.segments);

          // Did the user actually change the per-side totals? If not, we keep
          // the existing segments (preserving their order and pause structure)
          // and only rebase them if the start time shifted.
          const prevLSec = prev.reduce((a, s) => a + (s.side === 'left' ? s.to - s.from : 0), 0);
          const prevRSec = prev.reduce((a, s) => a + (s.side === 'right' ? s.to - s.from : 0), 0);
          const lSecRaw = Math.max(0, leftMin) * 60;
          const rSecRaw = Math.max(0, rightMin) * 60;
          const perSideEdited =
            Math.round(lSecRaw / 60) !== Math.round(prevLSec / 60) ||
            Math.round(rSecRaw / 60) !== Math.round(prevRSec / 60);
          const startShifted = effStart !== event.started_at;

          if (effEnd != null && perSideEdited) {
            // Rebuild segments. Side ordering preserves `event.type` as the
            // last-active side (a Switch updates `type` to the new side, so
            // the rebuilt order matches what the user just experienced).
            const window = Math.max(0, effEnd - effStart);
            // Clamp each side to the window individually first (handles the
            // case where one side alone exceeds window), then clamp the sum.
            let lSec = Math.min(lSecRaw, window);
            let rSec = Math.min(rSecRaw, window);
            if (lSec + rSec > window) {
              const lastIsRight = event.type === 'breast_right';
              if (lastIsRight) rSec = Math.max(0, window - lSec);
              else lSec = Math.max(0, window - rSec);
            }
            const otherSide: 'left' | 'right' =
              event.type === 'breast_right' ? 'left' : 'right';
            const otherSec = otherSide === 'left' ? lSec : rSec;
            const lastSide: 'left' | 'right' = otherSide === 'left' ? 'right' : 'left';
            const lastSec = lastSide === 'left' ? lSec : rSec;
            const newSegs: BreastSegment[] = [];
            if (otherSec > 0) newSegs.push({ side: otherSide, from: effStart, to: effStart + otherSec });
            if (lastSec > 0) newSegs.push({ side: lastSide, from: effEnd - lastSec, to: effEnd });
            patch.segments = newSegs;
            // Demote `type` to whichever side still has time.
            const currentType = event.type as 'breast_left' | 'breast_right';
            const promoted: 'breast_left' | 'breast_right' =
              lastSec > 0
                ? currentType
                : otherSec > 0
                  ? otherSide === 'left' ? 'breast_left' : 'breast_right'
                  : currentType;
            if (promoted !== currentType) patch.type = promoted;
          } else if (startShifted && prev.length > 0) {
            // User shifted the session window without touching per-side
            // totals. Slide every segment by the same delta so absolute
            // timestamps stay inside [effStart, effEnd]. Works for both
            // open and closed sessions.
            const delta = effStart - event.started_at;
            patch.segments = prev.map((s) => ({
              side: s.side,
              from: s.from + delta,
              to: s.to + delta,
            }));
          }
        }
        if ((event.notes ?? '') !== trimmedNotes) patch.notes = trimmedNotes || null;
        if (Object.keys(patch).length) await api(`/api/feed/${event.id}`, { method: 'PATCH', json: patch });
      } else if (event._t === 'pump') {
        if (startedAt !== event.started_at && startedAt != null) patch.started_at = startedAt;
        if ((endedAt ?? null) !== event.ended_at) patch.ended_at = endedAt;
        if ((event.side ?? null) !== pumpSide) patch.side = pumpSide;
        // Zero the inactive side per the side selection
        const effLeft = isPumpRight ? 0 : yieldLeft;
        const effRight = isPumpLeft ? 0 : yieldRight;
        if ((event.yield_left_ml ?? 0) !== effLeft) patch.yield_left_ml = effLeft;
        if ((event.yield_right_ml ?? 0) !== effRight) patch.yield_right_ml = effRight;
        const total = effLeft + effRight;
        if ((event.yield_ml ?? 0) !== total) patch.yield_ml = total;
        if ((event.notes ?? '') !== trimmedNotes) patch.notes = trimmedNotes || null;
        if (Object.keys(patch).length) await api(`/api/pump/${event.id}`, { method: 'PATCH', json: patch });
      } else if (event._t === 'diaper') {
        if (occurredAt !== event.occurred_at && occurredAt != null) patch.occurred_at = occurredAt;
        if (event.kind !== diaperKind) patch.kind = diaperKind;
        if (diaperKind === 'pee') {
          if (event.consistency != null) patch.consistency = null;
          if (event.color != null) patch.color = null;
        } else {
          if ((event.consistency ?? null) !== (consistency ?? null)) patch.consistency = consistency ?? null;
          if ((event.color ?? null) !== (color ?? null)) patch.color = color ?? null;
        }
        if (!!event.leak !== leak) patch.leak = leak;
        if ((event.notes ?? '') !== trimmedNotes) patch.notes = trimmedNotes || null;
        if (Object.keys(patch).length) await api(`/api/diaper/${event.id}`, { method: 'PATCH', json: patch });
      }

      inv();
      toast.success('Saved');
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  const titleByType =
    event._t === 'sleep' ? 'Edit sleep'
    : event._t === 'pump' ? 'Edit pump'
    : event._t === 'diaper' ? 'Edit diaper'
    : isBottle ? 'Edit bottle'
    : isSolid ? 'Edit solid'
    : 'Edit breast feed';

  const accent =
    event._t === 'sleep' ? 'sleep-night'
    : event._t === 'pump' ? 'pump'
    : event._t === 'diaper' ? (event.kind === 'pee' ? 'pee' : 'poop')
    : isBottle ? 'bottle'
    : isSolid ? 'solid'
    : 'breast';

  const Icn =
    event._t === 'sleep' ? Icon.moon
    : event._t === 'pump' ? Icon.pump
    : event._t === 'diaper' ? Icon.diaper
    : isBottle ? Icon.bottle
    : isSolid ? Icon.solid
    : Icon.breast;

  return (
    <SheetShell
      open={open}
      onClose={onClose}
      title={titleByType}
      tagline=""
      accent={accent}
      icon={(p) => <Icn {...p} />}
      primaryLabel={saving ? 'Saving…' : 'Save'}
      primaryDisabled={saving}
      onPrimary={save}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Time fields */}
        {event._t === 'diaper' && occurredAt != null && (
          <TimePicker label="Occurred at" value={occurredAt} onChange={setOccurredAt} />
        )}
        {event._t !== 'diaper' && startedAt != null && (
          <TimePicker label="Started at" value={startedAt} onChange={setStartedAt} />
        )}
        {event._t !== 'diaper' && (
          <FieldRow
            label="Ended"
            value={endedAt != null ? <EndedEditor value={endedAt} onChange={setEndedAt} /> : 'In progress'}
            hint={endedAt == null ? 'Save with End set to mark this finished' : undefined}
            right={endedAt == null && startedAt != null ? (
              <button
                type="button"
                className="lt-btn lt-btn-ghost"
                style={{ padding: '6px 10px', fontSize: 12 }}
                onClick={() => setEndedAt(Math.max(startedAt, Math.floor(Date.now() / 1000)))}
              >
                End now
              </button>
            ) : (
              <button
                type="button"
                className="lt-btn lt-btn-ghost"
                style={{ padding: '6px 10px', fontSize: 12 }}
                onClick={() => setEndedAt(null)}
              >
                Re-open
              </button>
            )}
          />
        )}

        {event._t === 'sleep' && (
          <SegRow
            label="Location"
            active={location}
            onChange={setLocation}
            options={SLEEP_LOCATIONS.map((id) => ({ id, label: cap(id) }))}
          />
        )}

        {isBreast && endedAt != null && startedAt != null && (
          <BreastPerSideEditor
            leftMin={leftMin}
            rightMin={rightMin}
            durationMin={Math.max(0, Math.floor((endedAt - startedAt) / 60))}
            onChange={(l, r) => { setLeftMin(l); setRightMin(r); }}
          />
        )}

        {isBottle && (
          <>
            <FieldRow
              label="Amount"
              value={
                <input
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={String(amount)}
                  onChange={(e) => {
                    const v = Number(e.target.value.replace(/[^0-9]/g, ''));
                    setAmount(Number.isFinite(v) ? Math.min(2000, Math.max(0, v)) : 0);
                  }}
                  className="lt-tabular"
                  style={{ width: '100%', fontSize: 17, color: 'var(--text-1)' }}
                />
              }
              hint="ml"
            />
            <SegRow
              label="Contents"
              active={content}
              onChange={setContent}
              options={BOTTLE_CONTENTS.map((id) => ({ id, label: cap(id) }))}
            />
          </>
        )}

        {event._t === 'pump' && (
          <>
            <SegRow
              label="Side"
              active={pumpSide}
              onChange={setPumpSide}
              options={PUMP_SIDES.map((id) => ({ id, label: cap(id) }))}
            />
            <div style={{ display: 'flex', gap: 10 }}>
              {pumpSide !== 'right' && (
                <YieldInput label="Left (ml)" value={yieldLeft} onChange={setYieldLeft} />
              )}
              {pumpSide !== 'left' && (
                <YieldInput label="Right (ml)" value={yieldRight} onChange={setYieldRight} />
              )}
            </div>
          </>
        )}

        {event._t === 'diaper' && (
          <>
            <SegRow
              label="Kind"
              active={diaperKind}
              onChange={setDiaperKind}
              options={DIAPER_KINDS.map((id) => ({
                id,
                label: cap(id),
                dot: true,
                color: id === 'pee' ? 'pee' : 'poop',
              }))}
            />
            {diaperKind !== 'pee' && (
              <>
                <SegRow
                  label="Consistency"
                  active={(consistency ?? 'soft') as DiaperConsistency}
                  onChange={(v) => setConsistency(v)}
                  options={DIAPER_CONSISTENCIES.map((id) => ({ id, label: cap(id) }))}
                />
                <SegRow
                  label="Colour"
                  active={(color ?? 'yellow') as DiaperColor}
                  onChange={(v) => setColor(v)}
                  options={DIAPER_COLORS.filter((c) => c !== 'other').map((id) => ({
                    id,
                    label: cap(id),
                  }))}
                />
              </>
            )}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                borderRadius: 14,
                background: 'var(--bg-card)',
                border: '1px solid var(--border)',
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 500 }}>Leak</div>
              <ToggleSwitch on={leak} onChange={setLeak} />
            </div>
          </>
        )}

        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--text-3)',
              textTransform: 'uppercase',
              letterSpacing: 0.04,
              marginBottom: 6,
              paddingLeft: 4,
            }}
          >
            Notes
          </div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="optional"
            style={{
              width: '100%',
              padding: '12px 14px',
              borderRadius: 14,
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              color: 'var(--text-1)',
              fontSize: 14,
              resize: 'none',
            }}
          />
        </div>
      </div>
    </SheetShell>
  );
}

function EndedEditor({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const d = new Date(value * 1000);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return (
    <input
      type="time"
      value={`${hh}:${mm}`}
      onChange={(e) => {
        const [h, m] = e.target.value.split(':').map(Number);
        if (Number.isNaN(h) || Number.isNaN(m)) return;
        const nd = new Date(value * 1000);
        nd.setHours(h, m, 0, 0);
        onChange(Math.floor(nd.getTime() / 1000));
      }}
      className="lt-mono lt-tabular"
      style={{ fontSize: 16, color: 'var(--text-1)' }}
    />
  );
}

function BreastPerSideEditor({
  leftMin,
  rightMin,
  durationMin,
  onChange,
}: {
  leftMin: number;
  rightMin: number;
  durationMin: number;
  onChange: (l: number, r: number) => void;
}) {
  const sum = leftMin + rightMin;
  const pause = Math.max(0, durationMin - sum);
  const over = sum > durationMin;
  return (
    <div
      style={{
        padding: '12px 14px',
        borderRadius: 14,
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--text-3)',
          textTransform: 'uppercase',
          letterSpacing: 0.04,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <span>Per-side</span>
        <span style={{ color: over ? 'var(--danger)' : 'var(--text-3)' }}>
          {sum}m / {durationMin}m{pause > 0 && !over ? ` · paused ${pause}m` : ''}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <MinuteInput label="Left" value={leftMin} onChange={(v) => onChange(v, rightMin)} max={durationMin} />
        <MinuteInput label="Right" value={rightMin} onChange={(v) => onChange(leftMin, v)} max={durationMin} />
      </div>
      {over && (
        <div style={{ fontSize: 11, color: 'var(--danger)' }}>
          Sum exceeds the feed duration. It'll be scaled down on save.
        </div>
      )}
    </div>
  );
}

function MinuteInput({
  label,
  value,
  onChange,
  max,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  max: number;
}) {
  return (
    <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{label}</span>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 4,
          padding: '8px 10px',
          borderRadius: 10,
          background: 'var(--bg-sunken)',
        }}
      >
        <input
          inputMode="numeric"
          pattern="[0-9]*"
          value={String(value)}
          onChange={(e) => {
            const n = Number(e.target.value.replace(/[^0-9]/g, ''));
            const clamped = Number.isFinite(n) ? Math.max(0, Math.min(max, n)) : 0;
            onChange(clamped);
          }}
          className="lt-tabular"
          style={{ width: '100%', fontSize: 20, color: 'var(--text-1)', background: 'transparent', border: 'none' }}
        />
        <span style={{ fontSize: 12, color: 'var(--text-3)' }}>m</span>
      </div>
    </label>
  );
}

function YieldInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <label
      style={{
        flex: 1,
        padding: '10px 12px',
        borderRadius: 12,
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--text-3)',
          textTransform: 'uppercase',
          letterSpacing: 0.04,
        }}
      >
        {label}
      </div>
      <input
        inputMode="numeric"
        pattern="[0-9]*"
        value={String(value)}
        onChange={(e) => {
          const v = Number(e.target.value.replace(/[^0-9]/g, ''));
          onChange(Number.isFinite(v) ? Math.min(2000, Math.max(0, v)) : 0);
        }}
        className="lt-serif lt-tabular"
        style={{
          width: '100%',
          fontSize: 22,
          color: 'var(--breast)',
          marginTop: 2,
        }}
      />
    </label>
  );
}
